import {memo, useContext} from 'react'

import cx from 'classnames'
import PropTypes from 'prop-types'

import Context from '@s-ui/react-context'
import ActionsAdFavorite, {actionAdFavoriteTrackingOrigins, actionAdFavoriteTypes} from '@adv-mt/mt-actions-ad-favorite'
import CardAdPrice from '@adv-mt/mt-card-ad-price'
import {useDateFormat} from '@adv-mt/mt-hook-use-date-format'
import LabelPriceDrop from '@adv-mt/mt-label-price-drop'
import TitleBasic, {titleBasicSizes, titleBasicTags} from '@adv-mt/mt-title-basic'

import SubscriptionPrice from './SubscriptionPrice.js'

const linkFactoryDefaultProp = ({children, ...rest}) => <a {...rest}>{children}</a>

const getDefaultSubscriptionOption = ({subscriptionPriceOptions = [], monthlySubscriptionFee}) => {
  const [subscriptionPriceOption] = subscriptionPriceOptions.filter(
    ({monthlyPrice}) => monthlyPrice === monthlySubscriptionFee
  )

  return subscriptionPriceOption
}

const PanelAdInfo = ({
  ad,
  extendedTrackingData = {},
  leadContactButtons,
  hasFinancingData = false,
  handleScrollToB2cFinancingPanel,
  handleScrollToC2cPanel,
  handleScrollToContactForm,
  handleScrollToFinancingTerms,
  handleScrollToPriceIndicatorPanel,
  handleSubscriptionOptionSelected,
  isFavorite,
  linkFactory: Link = linkFactoryDefaultProp,
  pageName,
  subscriptionPriceOptionSelected
}) => {
  const {modifiedDate, title, publishedDate, priceDropData = {}} = ad
  const {i18n, domain} = useContext(Context)
  const config = domain.get('config')

  const isSubscriptionAd = Boolean(ad.monthlySubscriptionFee)
  const {subscriptionPriceOptions} = ad

  const subscriptionPriceDefaultOption = getDefaultSubscriptionOption({
    subscriptionPriceOptions,
    monthlySubscriptionFee: ad.monthlySubscriptionFee
  })

  const publishedDateFormats = useDateFormat(new Date(publishedDate))
  const modifiedDateFormats = useDateFormat(new Date(modifiedDate))
  const {amount: priceDropAmount} = priceDropData
  const isPriceDropLabelShown = Boolean(priceDropAmount)
  const {IS_PRICE_DROP_BUTTON_ENABLED} = config.get('favorites')

  const panelAdInfoTitleClassName = cx('mt-PanelAdInfo-title', {
    'mt-PanelAdInfo-title--withPriceDrop': isPriceDropLabelShown
  })

  const trackingData = Link()?.props?.state

  return (
    <div className="mt-PanelAdInfo">
      <p className="mt-PanelAdInfo-published">
        {i18n.t('COMMON.DETAIL_DATE', {
          publishedDate: publishedDateFormats.relative,
          modifiedDate: modifiedDateFormats.relative
        })}
      </p>
      <div className={panelAdInfoTitleClassName}>
        <TitleBasic as={titleBasicTags.h1} size={titleBasicSizes.s} title={title} />
      </div>
      {isPriceDropLabelShown ? (
        <div className="mt-PanelAdInfo-priceDropLabelWrapper">
          <LabelPriceDrop amount={priceDropAmount} />
        </div>
      ) : null}
      <div className="mt-PanelAdInfo-price">
        <div className="mt-PanelAdInfo-priceCash">
          <CardAdPrice
            ad={ad}
            handleScrollToElements={{
              handleScrollToB2cFinancingPanel,
              handleScrollToC2cPanel,
              handleScrollToPriceIndicatorPanel
            }}
            handleScrollToContactForm={handleScrollToContactForm}
            handleScrollToFinancingTerms={handleScrollToFinancingTerms}
            hasFinancingData={hasFinancingData}
            isExtended
            instalmentPrice={ad?.financingData?.fee}
            subscriptionPriceOption={subscriptionPriceOptionSelected}
            pageName={pageName}
          />
        </div>
        {isSubscriptionAd ? (
          <SubscriptionPrice
            onSubscriptionPriceOptionSelected={handleSubscriptionOptionSelected}
            subscriptionPriceOptions={subscriptionPriceOptions}
            subscriptionPriceOptionSelected={subscriptionPriceOptionSelected || subscriptionPriceDefaultOption}
          />
        ) : null}
      </div>
      {/* temporarily hide price drop button for Subscription Ads until BE resolves the issues with this feature */}
      {IS_PRICE_DROP_BUTTON_ENABLED && !isSubscriptionAd ? (
        <div className="mt-PanelAdInfo-priceDropButtonWrapper">
          <ActionsAdFavorite
            ad={ad}
            extendedTrackingData={extendedTrackingData}
            isFavorite={isFavorite}
            trackingData={trackingData}
            trackingOrigin={actionAdFavoriteTrackingOrigins.DETAIL_PRICE_DROP_AD_FAVORITED}
            type={actionAdFavoriteTypes.PRICE_DROP_BUTTON}
          />
        </div>
      ) : null}
      {leadContactButtons}
    </div>
  )
}

PanelAdInfo.displayName = 'PanelAdInfo'
PanelAdInfo.propTypes = {
  ad: PropTypes.shape({
    priceDropData: PropTypes.object,
    monthlySubscriptionFee: PropTypes.number,
    subscriptionPriceOptions: PropTypes.object,
    id: PropTypes.string,
    title: PropTypes.string,
    publishedDate: PropTypes.object,
    priceDrop: PropTypes.object,
    modifiedDate: PropTypes.object,
    financingData: PropTypes.shape({
      availableTerm: PropTypes.bool,
      capital: PropTypes.number,
      coefficient: PropTypes.number,
      commissionEur: PropTypes.number,
      commission: PropTypes.number,
      fee: PropTypes.number,
      id: PropTypes.string,
      lender: PropTypes.string,
      maxEntry: PropTypes.number,
      minEntry: PropTypes.number,
      openingExpenses: PropTypes.number,
      openingPercentage: PropTypes.number,
      terms: PropTypes.number,
      tin: PropTypes.number,
      totalInterest: PropTypes.number
    }),
    isPrimaryCategory: PropTypes.bool
  }),
  extendedTrackingData: PropTypes.object,
  handleScrollToB2cFinancingPanel: PropTypes.func,
  handleScrollToC2cPanel: PropTypes.func,
  handleScrollToContactForm: PropTypes.func,
  handleScrollToFinancingTerms: PropTypes.func,
  handleScrollToPriceIndicatorPanel: PropTypes.func,
  handleSubscriptionOptionSelected: PropTypes.func,
  hasFinancingData: PropTypes.bool,
  isFavorite: PropTypes.bool,
  leadContactButtons: PropTypes.object,
  linkFactory: PropTypes.func,
  pageName: PropTypes.string,
  subscriptionPriceOptionSelected: PropTypes.object
}

const MemoPanelAdInfo = memo(PanelAdInfo)
MemoPanelAdInfo.displayName = 'PanelAdInfo'
export default MemoPanelAdInfo
